import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../theme';
import { useOwnerBookings } from '../../../hooks/venue/useOwnerBookings';
import { useOwnerDashboard } from '../../../hooks/venue/useOwnerDashboard';
import OwnerBookingCard from '../../../components/booking/OwnerBookingCard';
import { ArrowLeft, Calendar, Filter } from 'lucide-react';
import { BookingStatus } from '../../../types/venue';

type TabType = 'all' | 'pending' | 'confirmed' | 'completed';

const ManageBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { bookings, loading, error, fetchBookings, confirmBooking, completeBooking } =
    useOwnerBookings();
  const { venues } = useOwnerDashboard();

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  useEffect(() => {
    // Fetch venues for filter dropdown
    const fetchData = async () => {
      // This will be used for the venue filter
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch bookings based on active tab and filters
    const statusMap: Record<TabType, BookingStatus | undefined> = {
      all: undefined,
      pending: 'PENDING',
      confirmed: 'CONFIRMED',
      completed: 'COMPLETED',
    };

    fetchBookings({
      status: statusMap[activeTab],
      venueId: selectedVenue || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
  }, [activeTab, selectedVenue, dateFrom, dateTo, fetchBookings]);

  const handleConfirm = async (bookingId: string) => {
    const result = await confirmBooking(bookingId);
    if (result.success) {
      // Booking updated in state automatically
    }
  };

  const handleComplete = async (bookingId: string) => {
    const result = await completeBooking(bookingId);
    if (result.success) {
      // Booking updated in state automatically
    }
  };

  const handleClearFilters = () => {
    setSelectedVenue('');
    setDateFrom('');
    setDateTo('');
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const breadcrumbStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const breadcrumbLinkStyle: React.CSSProperties = {
    color: theme.colors.primary,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const filtersContainerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '20px',
    marginBottom: '24px',
    border: `2px solid ${theme.colors.border}`,
  };

  const filtersTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const filtersGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  };

  const filterGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const selectStyle: React.CSSProperties = {
    padding: '10px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
    cursor: 'pointer',
  };

  const inputStyle: React.CSSProperties = {
    padding: '10px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
  };

  const clearButtonStyle: React.CSSProperties = {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    alignSelf: 'flex-end',
  };

  const tabsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '32px',
    borderBottom: `2px solid ${theme.colors.border}`,
    paddingBottom: '0',
  };

  const getTabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: isActive ? theme.colors.primary : theme.colors.textSecondary,
    border: 'none',
    borderBottom: `3px solid ${isActive ? theme.colors.primary : 'transparent'}`,
    fontSize: theme.fonts.sizeBody,
    fontWeight: isActive ? theme.fonts.weightBold : theme.fonts.weightNormal,
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const countBadgeStyle: React.CSSProperties = {
    marginLeft: '8px',
    padding: '2px 8px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    borderRadius: '12px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
  };

  const emptyIconStyle: React.CSSProperties = {
    marginBottom: '16px',
    color: theme.colors.textSecondary,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const getEmptyMessage = (tab: TabType): string => {
    switch (tab) {
      case 'all':
        return 'No hay reservas aún';
      case 'pending':
        return 'No hay reservas pendientes';
      case 'confirmed':
        return 'No hay reservas confirmadas';
      case 'completed':
        return 'No hay reservas completadas';
      default:
        return 'No hay reservas';
    }
  };

  // Count bookings by status for badges
  const pendingCount = bookings.filter((b) => b.status === 'PENDING').length;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={breadcrumbStyle}>
          <a
            style={breadcrumbLinkStyle}
            onClick={() => navigate('/owner/dashboard')}
          >
            Dashboard
          </a>
          <span>/</span>
          <span>Gestionar reservas</span>
        </div>
        <h1 style={titleStyle}>GESTIONAR RESERVAS</h1>
        <p style={subtitleStyle}>
          Confirmá, completá y gestioná todas las reservas de tus canchas
        </p>
      </div>

      {/* Filters */}
      <div style={filtersContainerStyle}>
        <div style={filtersTitleStyle}>
          <Filter size={20} />
          Filtros
        </div>
        <div style={filtersGridStyle}>
          <div style={filterGroupStyle}>
            <label style={labelStyle}>Cancha</label>
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              style={selectStyle}
            >
              <option value="">Todas las canchas</option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </div>

          <div style={filterGroupStyle}>
            <label style={labelStyle}>Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={filterGroupStyle}>
            <label style={labelStyle}>Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={inputStyle}
            />
          </div>

          <button
            style={clearButtonStyle}
            onClick={handleClearFilters}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={tabsContainerStyle}>
        <button
          style={getTabStyle(activeTab === 'all')}
          onClick={() => setActiveTab('all')}
          onMouseEnter={(e) => {
            if (activeTab !== 'all') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'all') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Todas
          {activeTab === 'all' && (
            <span style={countBadgeStyle}>{bookings.length}</span>
          )}
        </button>
        <button
          style={getTabStyle(activeTab === 'pending')}
          onClick={() => setActiveTab('pending')}
          onMouseEnter={(e) => {
            if (activeTab !== 'pending') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'pending') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Pendientes
          {pendingCount > 0 && (
            <span style={countBadgeStyle}>{pendingCount}</span>
          )}
        </button>
        <button
          style={getTabStyle(activeTab === 'confirmed')}
          onClick={() => setActiveTab('confirmed')}
          onMouseEnter={(e) => {
            if (activeTab !== 'confirmed') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'confirmed') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Confirmadas
        </button>
        <button
          style={getTabStyle(activeTab === 'completed')}
          onClick={() => setActiveTab('completed')}
          onMouseEnter={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Completadas
        </button>
      </div>

      {/* Loading State */}
      {loading && <div style={loadingStyle}>Cargando reservas...</div>}

      {/* Error State */}
      {error && !loading && <div style={errorStyle}>{error}</div>}

      {/* Bookings List */}
      {!loading && !error && (
        <>
          {bookings.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <Calendar size={64} />
              </div>
              <div style={emptyTitleStyle}>{getEmptyMessage(activeTab)}</div>
              <div style={emptyMessageStyle}>
                Las reservas aparecerán aquí cuando los usuarios reserven tus canchas
              </div>
            </div>
          ) : (
            <>
              {bookings.map((booking) => (
                <OwnerBookingCard
                  key={booking.id}
                  booking={booking}
                  onConfirm={handleConfirm}
                  onComplete={handleComplete}
                  highlighted={booking.status === 'PENDING'}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ManageBookingsPage;
